import type { ReactNode } from 'react';
import { InfoIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface InfoCalloutProps {
  /** `note` for standing information; `status` when it reports on the data beside it. */
  role?: 'note' | 'status';
  /** One trailing action, at most. */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * A calm note that is information, not an error (spec 226 FR-016): a muted
 * surface, the information icon and body text, with room for one action. It
 * never wears the destructive colour — the dashed and red boxes it replaces
 * made a reader look for something wrong where nothing was.
 */
export function InfoCallout({
  role = 'note',
  action,
  children,
  className,
}: InfoCalloutProps) {
  return (
    <div
      role={role}
      className={cn(
        'bg-muted/40 border-border text-foreground flex items-start gap-3 rounded-lg border px-4 py-3 text-sm',
        className,
      )}
    >
      <InfoIcon
        aria-hidden="true"
        strokeWidth={1.5}
        className="text-muted-foreground mt-0.5 size-4 shrink-0"
      />
      <div className="flex-1">{children}</div>
      {action}
    </div>
  );
}
